// src/components/SettingsPanel.js

import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import * as FaIcons from 'react-icons/fa';

// Define limits matching context/API
const MIN_RATE = 0.25; const MAX_RATE = 4.0; const RATE_STEP = 0.05;
const MIN_PITCH = -20.0; const MAX_PITCH = 20.0; const PITCH_STEP = 0.5;

// Helper to format numbers
const formatNumber = (n, p = 1) => { const num = Number(n); return isNaN(num)?'N/A':num.toFixed(p); }

function SettingsPanel({ className, style }) {
  // Get state and setters from the context
  const {
      settingsState,
      setVoice, availableVoices,
      setSpeakingRate, setPitch,
      playSampleAudio,
      setLanguage, // Get language setter
      availableLanguages // Get language list
  } = useSettings();

  // Destructure state for easier use
  const {
      voice, speakingRate, pitch, language, // Include language
      isSampleAudioLoading, sampleAudioError
  } = settingsState;

  // Event Handlers
  const handleVoiceChange = (event) => setVoice(event.target.value);
  const handleRateChange = (event) => setSpeakingRate(event.target.value);
  const handlePitchChange = (event) => setPitch(event.target.value);
  const handleLanguageChange = (langCode) => { setLanguage(langCode); }; // Handler for lang buttons

  // Styles (consider moving to CSS)
  const labelStyle = { marginRight:'10px',fontWeight:'bold',display:'block',marginBottom:'8px',color:'#333' };
  const selectStyle = { padding:'8px',borderRadius:'4px',border:'1px solid #ccc',width:'100%',boxSizing:'border-box',fontSize:'1em' };
  const rangeContainerStyle = { display:'flex',alignItems:'center',width:'100%' };
  const rangeStyle = { flexGrow: 1,cursor:'pointer',height:'8px',verticalAlign:'middle'};
  const valueStyle = { marginLeft:'10px',fontVariantNumeric:'tabular-nums',minWidth:'45px',display:'inline-block',textAlign:'right',fontSize:'0.9em',color:'#333' };
  const settingBlockStyle = { marginBottom: '18px' };
  const panelStyle = { padding:'20px',backgroundColor:'#ffffff',border:'1px solid #ccc',borderRadius:'8px' };
  const playButtonStyle = { display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',padding:'10px 15px',marginTop:'15px',width:'100%',fontSize:'1em',fontWeight:'bold',cursor:'pointer',border:'1px solid #1c7430',backgroundColor:'#28a745',color:'white',borderRadius:'4px',transition:'background-color 0.2s ease' };
  const disabledPlayButtonStyle = { ...playButtonStyle, backgroundColor:'#6c757d',borderColor:'#5a6268',cursor:'not-allowed',opacity:0.65 };
  const errorTextStyle = { color:'#dc3545',fontSize:'0.9em',marginTop:'5px' };
  // Styles for language buttons (single column layout)
  const langContainerStyle = { display:'flex',flexDirection:'column',gap:'6px' };
  const langButtonStyle = { padding:'8px 12px',fontSize:'0.95em',cursor:'pointer',borderRadius:'4px',border:'1px solid #ccc',backgroundColor:'#f8f9fa',color:'#212529',textAlign:'left',width:'100%',boxSizing:'border-box',transition:'all 0.2s ease' };
  const activeLangButtonStyle = { ...langButtonStyle, backgroundColor:'#0d6efd',color:'white',borderColor:'#0a58ca',fontWeight:'bold' };


  // Determine if play button should be disabled
  const isPlayDisabled = isSampleAudioLoading;

  return (
    // Apply passed className and style from Layout, plus base panel styles
    <div className={className} style={{ ...panelStyle, ...style }}>
      <h4>Settings</h4>

      {/* Language Selection */}
      <div style={settingBlockStyle}>
          <label style={labelStyle}>Language:</label>
          <div style={langContainerStyle}>
              {availableLanguages.map(lang => (
                  <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      style={lang.code === language ? activeLangButtonStyle : langButtonStyle}
                      aria-pressed={lang.code === language}
                  >
                      {lang.name}
                  </button>
              ))}
          </div>
      </div>

      {/* Voice Selection */}
      <div style={settingBlockStyle}>
        <label htmlFor="voiceSelect" style={labelStyle}>Character Voice:</label>
        <select id="voiceSelect" value={voice} onChange={handleVoiceChange} style={selectStyle} aria-label="Select Character Voice">
          {availableVoices.map((opt) => ( <option key={opt.value} value={opt.value}>{opt.name}</option> ))}
        </select>
      </div>

      {/* Speaking Rate Slider */}
      <div style={settingBlockStyle}>
        <label htmlFor="rateSlider" style={labelStyle}>Speaking Speed:</label>
         <div style={rangeContainerStyle}>
             <input type="range" id="rateSlider" min={MIN_RATE} max={MAX_RATE} step={RATE_STEP} value={speakingRate} onChange={handleRateChange} style={rangeStyle} aria-label="Speaking Speed Slider"/>
             <span style={valueStyle} aria-hidden="true">{formatNumber(speakingRate, 2)}x</span>
         </div>
      </div>

      {/* Pitch Slider */}
       <div style={settingBlockStyle}>
        <label htmlFor="pitchSlider" style={labelStyle}>Pitch:</label>
          <div style={rangeContainerStyle}>
             <input type="range" id="pitchSlider" min={MIN_PITCH} max={MAX_PITCH} step={PITCH_STEP} value={pitch} onChange={handlePitchChange} style={rangeStyle} aria-label="Pitch Slider"/>
             <span style={valueStyle} aria-hidden="true">{formatNumber(pitch, 1)}</span>
          </div>
      </div>

       {/* Play Sample Button */}
       <div style={settingBlockStyle}>
           <button onClick={playSampleAudio} style={isPlayDisabled ? disabledPlayButtonStyle : playButtonStyle} disabled={isPlayDisabled} title="Play 'Hello and welcome!' with current settings">
                <FaIcons.FaPlay style={{ marginRight: '5px' }}/>
                {isSampleAudioLoading ? 'Loading...' : 'Play Sample'}
           </button>
           {sampleAudioError && <p style={errorTextStyle}>Error: {sampleAudioError}</p>}
       </div>

    </div>
  );
}

export default SettingsPanel;